import { useState } from "react";
import { Link } from "react-router-dom";
import { useRecentFailures, type RecentFailureDto } from "../api/systemStatus.js";
import { BackButton } from "../components/BackButton.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { formatRelative, kindLabel } from "../lib/format.js";

const HOURS_OPTS: Array<{ label: string; value: number }> = [
  { label: "6ч", value: 6 },
  { label: "24ч", value: 24 },
  { label: "3д", value: 72 },
  { label: "7д", value: 168 },
];

// Лента последних фейлов. Closes «алёрт прилетел, что случилось?» — UI
// сразу показывает error_message + видно retried/нет, и клик ведёт в Detail.
export function FailuresPage() {
  const [hours, setHours] = useState<number>(24);
  const q = useRecentFailures({ hours, limit: 100 });

  return (
    <div className="space-y-3 p-3">
      <BackButton to="/" />
      <h1 className="text-lg font-bold">Последние фейлы</h1>

      <div className="flex gap-1 rounded-xl bg-bg-secondary p-1">
        {HOURS_OPTS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setHours(opt.value)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-sm transition ${
              hours === opt.value ? "bg-bg font-medium" : "text-hint"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {q.isLoading && <Loading />}
      {q.isError && <ErrorState error={q.error} />}
      {q.isSuccess && q.data.length === 0 && (
        <div className="rounded-lg bg-bg-secondary p-6 text-center text-sm text-hint">
          ✓ Никаких фейлов за выбранный период
        </div>
      )}

      <div className="space-y-2">
        {q.data?.map((f) => <FailureCard key={f.id} f={f} />)}
      </div>
    </div>
  );
}

function FailureCard({ f }: { f: RecentFailureDto }) {
  const resolved = f.hasRetriedSuccessfully;
  return (
    <Link
      to={`/items/${f.itemId}`}
      className={`block rounded-xl border p-3 text-sm transition active:scale-[0.99] ${
        resolved
          ? "border-bg-secondary bg-bg-secondary opacity-70"
          : "border-destructive/25 bg-destructive/5"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-hint">
            <span>{kindLabel(f.itemKind)}</span>
            {f.errorClass && (
              <span
                className={`rounded-full px-1.5 py-0.5 ${
                  resolved ? "bg-bg text-text" : "bg-destructive/20 text-destructive"
                }`}
              >
                {f.errorClass}
              </span>
            )}
            {resolved && (
              <span className="rounded-full bg-button/15 px-1.5 py-0.5 text-button">
                ✓ retried
              </span>
            )}
            <span className="text-hint">attempt {f.attempt}</span>
          </div>
          <div className="mt-1 truncate font-medium">{f.itemDisplayName}</div>
          {f.errorMessage && (
            <div className="mt-1 line-clamp-2 break-words text-xs text-hint">
              {f.errorMessage}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right text-[10px] text-hint">
          {formatRelative(f.createdAt)}
        </div>
      </div>
    </Link>
  );
}
